import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { v2Api } from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { IUserSavingsAccountsData } from '../types';
import {
  getApiErrorMessage,
  getSavingsAuthConfig,
  getSavingsBusinessId,
} from '../utils';

interface IGetBucketsResponse {
  status?: boolean;
  message?: string;
  data?: IUserSavingsAccountsData;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetBucketsResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getBucketsAsync = createAsyncThunk(
  'buckets',
  async (_: void, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const businessId = getSavingsBusinessId(state);

      const { data } = await v2Api.get<IGetBucketsResponse>(
        `/savings/users/${businessId}/accounts`,
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const bucketsGetSlice = createSlice({
  name: 'buckets/get',
  initialState,
  reducers: {
    resetGetBuckets: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBucketsAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getBucketsAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetBucketsResponse;
      })
      .addCase(getBucketsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetGetBuckets } = bucketsGetSlice.actions;

export default bucketsGetSlice.reducer;
